import { zodResolver } from '@hookform/resolvers/zod';
import { useEffect, useRef } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { Form } from '../../../../../design-system/form/form-primitives';
import { Stack } from '../../../../../design-system/stack/stack';
import { CategoryView } from '../../../../../types/entity-types';
import { CategorySelectField } from './category-select-field';

const formSchema = z.object({
  categoryId: z.string({ required_error: 'Category is required' }),
});

type ChangeVendorCategoryFormValues = z.infer<typeof formSchema>;

type ChangeVendorCategoryProps = {
  category?: CategoryView;
  categories: CategoryView[];
  onValueChange: (categoryId: string) => void;
};

export const ChangeVendorCategory = ({ category, categories, onValueChange }: ChangeVendorCategoryProps) => {
  const form = useForm<ChangeVendorCategoryFormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      categoryId: category?.id,
    },
    mode: 'onChange',
  });

  // Guard to avoid duplicate emission in StrictMode and across re-renders
  const lastEmittedCategoryIdRef = useRef<string | undefined>(category?.id);

  // Reset form when the incoming category changes (e.g., navigating between transactions)
  useEffect(() => {
    form.reset({ categoryId: category?.id });
    lastEmittedCategoryIdRef.current = category?.id;
  }, [category?.id, form]);

  // Watch for changes and trigger the callback exactly once per unique selection
  useEffect(() => {
    const subscription = form.watch((data) => {
      const nextCategoryId = data.categoryId;

      if (!nextCategoryId) return;
      if (nextCategoryId === category?.id) return;
      if (lastEmittedCategoryIdRef.current === nextCategoryId) return;

      lastEmittedCategoryIdRef.current = nextCategoryId;
      onValueChange(nextCategoryId);
    });

    return () => subscription.unsubscribe();
    // It's important not to include onValueChange in deps to avoid re-subscribing on each render
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form, category?.id]);

  return (
    <Form {...form}>
      <form className="w-full">
        <Stack space={4} align="stretch">
          <CategorySelectField
            inputName="categoryId"
            initialValue={category}
            vendorCategoryId={category?.id}
            categories={categories}
          />
        </Stack>
      </form>
    </Form>
  );
};
